import { useEffect, useMemo, useState } from 'react';
import '../styles/dashboard-progress-management.css';
import EditableRecordTable from '../components/EditableRecordTable.jsx';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import AdvancedFilterPanel from '../components/AdvancedFilterPanel.jsx';
import useAdvancedFilter from '../hooks/useAdvancedFilter.js';
import {
  createRisk,
  deleteRisk,
  getActors,
  getCauses,
  getEmployees,
  getImpacts,
  getLargeCategories,
  getMiddleCategories,
  getScheduleOemRegionPairs,
  getPolicies,
  getProbabilities,
  getRisks,
  getSmallCategories,
  getStatuses,
  updateRisk,
} from '../api/dashboardApi.js';
import { exportToExcel } from '../utils/exportUtils.js';
import ExportConfirmPopup from '../components/ExportConfirmPopup.jsx';
import { getUserName } from '../../../auth/auth.js';
import { canEdit } from '../../../auth/permissions.js';
import { buildLookupMap, buildSmallCategoryOptionsByMiddleId } from '../utils/converters.js';
import {
  buildSelectOptions,
  buildSmallCategoryLookup,
  buildMiddleCategoryIdLookup,
  buildMiddleCategoryOptionsByLarge,
  toPayload,
} from '../utils/riskConverters.js';

const BASE_COLUMNS = [
  { key: 'deadlineCheck', label: '期限チェック', type: 'text', width: '120px', readOnly: true },
  { key: 'noId', label: 'ID', type: 'text', width: '80px', readOnly: true },
  { key: 'title', label: 'タイトル', type: 'text', width: '220px', required: true },
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'actor', label: '領域', type: 'select', width: '130px', required: true },
  { key: 'largeCategory', label: '大項目', type: 'select', width: '160px' },
  { key: 'middleCategory', label: '中項目', type: 'select', width: '160px' },
  { key: 'smallItem', label: '小項目', type: 'select', width: '180px' },
  { key: 'projectRisk', label: 'PJ全体リスク', type: 'checkbox', width: '150px' },
  { key: 'owner', label: '担当者', type: 'tags', width: '170px', required: true },
  { key: 'status', label: 'ステータス', type: 'select', width: '130px', required: true },
  { key: 'riskOrigin', label: 'リスク起因', type: 'select', width: '160px', required: true },
  { key: 'riskLevel', label: 'リスクレベル', type: 'text', width: '100px', readOnly: true },
  { key: 'probability', label: '発生確率', type: 'select', width: '120px', required: true },
  { key: 'impact', label: '影響度', type: 'select', width: '120px', required: true },
  { key: 'policyDecisionDeadline', label: '対応方針決定期限', type: 'date', width: '170px' },
  { key: 'policyDecisionDate', label: '対応方針決定日', type: 'date', width: '160px' },
  { key: 'responsePolicy', label: 'リスク対応方針', type: 'select', width: '180px' },
  { key: 'policyDetail', label: '対応方針詳細', type: 'text', width: '260px' },
  { key: 'dueDate', label: '対応期限', type: 'date', width: '140px', required: true },
  { key: 'responseStartDate', label: '対応開始日', type: 'date', width: '140px' },
  { key: 'actorCompletedAt', label: '担当領域完了日', type: 'date', width: '160px' },
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'emergenceIssueDate', label: '顕在化による課題移行日', type: 'date', width: '210px' },
  { key: 'pmoConfirmCompletedAt', label: 'PMO確認完了日', type: 'date', width: '160px' },
];

function createEmptyRow(/* istanbul ignore next -- 呼び出し元は常に defaults を渡す */ defaults = {}) {
  return BASE_COLUMNS.reduce((acc, column) => {
    if (!(column.key in acc)) {
      acc[column.key] = column.type === 'checkbox' ? false : '';
    }
    return acc;
  }, { ...defaults, __isNew: true });
}

export default function DashboardRiskManagementPage() {
  const [rows, setRows] = useState([]);
  const [editingRows, setEditingRows] = useState([]);
  const [statusByName, setStatusByName] = useState(new Map());
  const [actorByName, setActorByName] = useState(new Map());
  const [causeByName, setCauseByName] = useState(new Map());
  const [impactByName, setImpactByName] = useState(new Map());
  const [probabilityByName, setProbabilityByName] = useState(new Map());
  const [policyByName, setPolicyByName] = useState(new Map());
  const [largeCategoryByName, setLargeCategoryByName] = useState(new Map());
  const [middleCategoryIdLookup, setMiddleCategoryIdLookup] = useState({
    pairMap: new Map(), byNameMap: new Map(),
  });
  const [smallCategoryLookup, setSmallCategoryLookup] = useState({
    pairMap: new Map(), byNameMap: new Map(),
  });
  const [employeeByName, setEmployeeByName] = useState(new Map());
  const [employeeOptions, setEmployeeOptions] = useState(['']);
  const [oemRegionOptions, setOemRegionOptions] = useState(['']);
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());
  const [statusOptions, setStatusOptions] = useState(['']);
  const [actorOptions, setActorOptions] = useState(['']);
  const [causeOptions, setCauseOptions] = useState(['']);
  const [impactOptions, setImpactOptions] = useState(['']);
  const [probabilityOptions, setProbabilityOptions] = useState(['']);
  const [policyOptions, setPolicyOptions] = useState(['']);
  const [defaultCreateValues, setDefaultCreateValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [addSignal, setAddSignal] = useState(0);
  const [largeCategoryOptions, setLargeCategoryOptions] = useState(['']);
  const [middleCategoryOptionsByLarge, setMiddleCategoryOptionsByLarge] = useState({});
  const [smallCategoryOptionsByMiddleId, setSmallCategoryOptionsByMiddleId] = useState({});
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const {
    filterValues: riskFilterValues,
    filterMode,
    setFilterMode,
    handleFieldChange: handleRiskFilterChange,
    clearFilters,
    buildFilterPredicate,
  } = useAdvancedFilter({
    initialValues: {
      deadlineCheck: [],
      riskLevel: [],
      oemRegion: [],
      status: [],
      actor: [],
      largeCategory: [],
      middleCategory: [],
      riskOrigin: [],
      impact: [],
      probability: [],
      responsePolicy: [],
      owner: getUserName() ? [getUserName()] : [],
      projectRisk: [],
      date_from: '',
      date_to: '',
      keyword: '',
    },
    initialMode: 'AND',
  });

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [
          data,
          statusData,
          actorData,
          causeData,
          impactData,
          probabilityData,
          policyData,
          oemRegionPairs,
          largeCategoryData,
          middleCategoryData,
          employeeData,
          smallCategoryData,
        ] = await Promise.all([
          getRisks(),
          getStatuses(),
          getActors(),
          getCauses(),
          getImpacts(),
          getProbabilities(),
          getPolicies(),
          getScheduleOemRegionPairs(),
          getLargeCategories(),
          getMiddleCategories(),
          getEmployees(),
          getSmallCategories(),
        ]);
        if (!active) return;
        setRows(data);
        setStatusByName(buildLookupMap(statusData, 'status'));
        setActorByName(buildLookupMap(actorData, 'actor'));
        setCauseByName(buildLookupMap(causeData, 'cause'));
        setImpactByName(buildLookupMap(impactData, 'impact'));
        setProbabilityByName(buildLookupMap(probabilityData, 'probability'));
        setPolicyByName(buildLookupMap(policyData, 'policy'));
        setLargeCategoryByName(buildLookupMap(largeCategoryData, 'large_category'));
        setMiddleCategoryIdLookup(buildMiddleCategoryIdLookup(middleCategoryData));
        setSmallCategoryLookup(buildSmallCategoryLookup(smallCategoryData));
        setStatusOptions(['', ...statusData.map((item) => item.status).filter(Boolean)]);
        setActorOptions(['', ...actorData.map((item) => item.actor).filter(Boolean)]);
        setCauseOptions(['', ...causeData.map((item) => item.cause).filter(Boolean)]);
        setImpactOptions(['', ...impactData.map((item) => item.impact).filter(Boolean)]);
        setProbabilityOptions(['', ...probabilityData.map((item) => item.probability).filter(Boolean)]);
        setPolicyOptions(['', ...policyData.map((item) => item.policy).filter(Boolean)]);
        setLargeCategoryOptions([
          '',
          ...Array.from(new Set(largeCategoryData.map((item) => item.large_category).filter(Boolean))),
        ]);
        setMiddleCategoryOptionsByLarge(buildMiddleCategoryOptionsByLarge(middleCategoryData));
        setSmallCategoryOptionsByMiddleId(buildSmallCategoryOptionsByMiddleId(smallCategoryData));
        setEmployeeByName(buildLookupMap(employeeData, 'user_name', 'employee_id'));
        setEmployeeOptions(['', ...employeeData.map((item) => item.user_name).filter(Boolean)]);
        const oemRegionOpts = [''];
        const oemRegionMap = new Map();
        oemRegionPairs.forEach((p) => {
          if (!oemRegionMap.has(p.label)) {
            oemRegionOpts.push(p.label);
            oemRegionMap.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
          }
        });
        setOemRegionOptions(oemRegionOpts);
        setOemRegionLookup(oemRegionMap);
        setDefaultCreateValues({
          oemId: oemRegionPairs[0]?.oem_id,
          regionId: oemRegionPairs[0]?.region_id,
          status: '未着手',
          impact: '高',
          probability: '高',
        });
      } catch (err) {
        if (!active) return;
        setError('リスク管理データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const selectColumnKeys = useMemo(
    () => BASE_COLUMNS.filter((column) => column.type === 'select').map((column) => column.key),
    [],
  );

  const selectOptions = useMemo(
    () => buildSelectOptions(editingRows.length ? editingRows : rows, selectColumnKeys),
    [editingRows, rows, selectColumnKeys],
  );

  const columns = useMemo(
    () =>
      BASE_COLUMNS.map((col) => {
        if (col.key === 'largeCategory') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            options: largeCategoryOptions,
            onChange: ({ nextRow, value }) => {
              const middleOptions = middleCategoryOptionsByLarge[value] || [''];
              const keepMiddle = middleOptions.includes(nextRow.middleCategory || '');

              // 大項目が変わると中項目IDが変わり小項目候補も変わる。
              // 同名中項目の衝突を避けるため中項目IDでスコープする。
              const middleId = keepMiddle
                ? middleCategoryIdLookup.pairMap.get(`${/* istanbul ignore next -- 大項目・中項目は選択式で未選択は空文字として入る */ value || ''}::${nextRow.middleCategory || ''}`)
                : null;
              const smallOptions = (middleId != null && smallCategoryOptionsByMiddleId[middleId]) || [''];
              const keepSmall = keepMiddle && smallOptions.includes(nextRow.smallItem || '');

              return {
                ...nextRow,
                middleCategory: keepMiddle ? (nextRow.middleCategory || '') : '',
                smallItem: keepSmall ? (nextRow.smallItem || '') : '',
              };
            },
          };
        }

        if (col.key === 'middleCategory') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            getOptions: ({ row }) => middleCategoryOptionsByLarge[row?.largeCategory] || [''],
            onChange: ({ nextRow, value }) => {
              /* istanbul ignore next -- 大項目・中項目は選択式で未選択は空文字として入る */
              const middleId = middleCategoryIdLookup.pairMap.get(`${nextRow.largeCategory || ''}::${value || ''}`);
              const smallOptions = (middleId != null && smallCategoryOptionsByMiddleId[middleId]) || [''];
              const keepSmall = smallOptions.includes(nextRow.smallItem || '');
              return {
                ...nextRow,
                smallItem: keepSmall ? (nextRow.smallItem || '') : '',
              };
            },
          };
        }

        if (col.key === 'smallItem') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            getOptions: ({ row }) => {
              // 小項目候補は選択中の中項目に所属するものだけに限定する（マスタ由来・中項目IDキー）
              const middleId = middleCategoryIdLookup.pairMap.get(`${row?.largeCategory || ''}::${row?.middleCategory || ''}`);
              return (middleId != null && smallCategoryOptionsByMiddleId[middleId]) || [''];
            },
          };
        }

        if (col.key === 'owner') {
          return {
            ...col,
            width: /* istanbul ignore next -- この列は BASE_COLUMNS で width を指定済み */ (col.width || '148px'),
            getOptions: () => employeeOptions,
          };
        }

        return {
          ...col,
          width: (col.width || '148px'),
          options:
            col.key === 'oemRegion'
              ? oemRegionOptions
              : col.key === 'status'
              ? statusOptions
              : col.key === 'actor'
              ? actorOptions
              : col.key === 'riskOrigin'
              ? causeOptions
              : col.key === 'impact'
              ? impactOptions
              : col.key === 'probability'
              ? probabilityOptions
              : col.key === 'responsePolicy'
              ? policyOptions
              : /* istanbul ignore next -- select 列はここまでの分岐で網羅している */ col.type === 'select'
              ? (selectOptions[col.key] || [''])
              : undefined,
        };
      }),
    [
      actorOptions,
      causeOptions,
      employeeOptions,
      impactOptions,
      largeCategoryOptions,
      middleCategoryIdLookup,
      middleCategoryOptionsByLarge,
      oemRegionOptions,
      policyOptions,
      probabilityOptions,
      selectOptions,
      smallCategoryOptionsByMiddleId,
      statusOptions,
    ],
  );

  const deadlineCheckOptions = useMemo(() => {
    const values = new Set();
    rows.forEach((row) => { if (row.deadlineCheck) values.add(row.deadlineCheck); });
    return Array.from(values);
  }, [rows]);

  const riskLevelOptions = useMemo(() => {
    const values = new Set();
    rows.forEach((row) => { if (row.riskLevel) values.add(row.riskLevel); });
    return Array.from(values);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const predicate = buildFilterPredicate({
      deadlineCheck: (row, vals) => vals.includes(row.deadlineCheck),
      riskLevel: (row, vals) => vals.includes(row.riskLevel),
      oemRegion: (row, vals) => vals.includes(row.oemRegion),
      status: (row, vals) => vals.includes(row.status),
      actor: (row, vals) => vals.includes(row.actor),
      largeCategory: (row, vals) => vals.includes(row.largeCategory),
      middleCategory: (row, vals) => vals.includes(row.middleCategory),
      riskOrigin: (row, vals) => vals.includes(row.riskOrigin),
      impact: (row, vals) => vals.includes(row.impact),
      probability: (row, vals) => vals.includes(row.probability),
      responsePolicy: (row, vals) => vals.includes(row.responsePolicy),
      owner: (row, vals) => vals.some((v) => (row.owner || '').includes(v)),
      projectRisk: (row, vals) => {
        if (vals.includes('true') && row.projectRisk) return true;
        if (vals.includes('false') && !row.projectRisk) return true;
        return false;
      },
      date_from: (row, val) => {
        // 期限チェックの判定軸に合わせて対応期限を優先する
        const d = row.dueDate || row.policyDecisionDeadline;
        return d && d >= val;
      },
      date_to: (row, val) => {
        // 期限チェックの判定軸に合わせて対応期限を優先する
        const d = row.dueDate || row.policyDecisionDeadline;
        return d && d <= val;
      },
      keyword: (row, val) => {
        const keyword = val.trim().toLowerCase();
        if (!keyword) return true;
        const target = [row.title, row.smallItem, row.policyDetail]
          .filter(Boolean).join(' ').toLowerCase();
        return target.includes(keyword);
      },
    });
    return rows.filter(predicate);
  }, [rows, buildFilterPredicate]);

  const riskFilterFields = useMemo(
    () => [
      {
        key: 'deadlineCheck',
        label: '期限チェック',
        type: 'multiselect',
        options: deadlineCheckOptions.map((o) => ({ value: o, label: o })),
      },
      {
        key: 'riskLevel',
        label: 'リスクレベル',
        type: 'multiselect',
        options: riskLevelOptions.map((o) => ({ value: o, label: o })),
      },
      {
        key: 'oemRegion',
        label: 'OEM×対地',
        type: 'multiselect',
        options: oemRegionOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'status',
        label: 'ステータス',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ statusOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'actor',
        label: '領域',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ actorOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'largeCategory',
        label: '大項目',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ largeCategoryOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'middleCategory',
        label: '中項目',
        type: 'multiselect',
        getOptions: ({ values }) => {
          /* istanbul ignore next -- 高度フィルターは全キーを配列で初期化するため undefined にならない */
          const selectedLarge = values.largeCategory || [];
          let opts;
          if (selectedLarge.length > 0) {
            const merged = new Set();
            selectedLarge.forEach((lg) => {
              (middleCategoryOptionsByLarge[lg] || []).forEach((m) => { if (m) merged.add(m); });
            });
            opts = Array.from(merged);
          } else {
            opts = (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.middleCategory || []).filter(Boolean);
          }
          return opts.map((o) => ({ value: o, label: o }));
        },
      },
      {
        key: 'riskOrigin',
        label: 'リスク起因',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ causeOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'impact',
        label: '影響度',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ impactOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'probability',
        label: '発生確率',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ probabilityOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'responsePolicy',
        label: 'リスク対応方針',
        type: 'multiselect',
        options: (/* istanbul ignore next -- useState(['']) 初期化＋setter は常に配列を渡すため undefined にならない */ policyOptions || []).filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'owner',
        label: '担当者',
        type: 'multiselect',
        options: employeeOptions.filter(Boolean).map((o) => ({ value: o, label: o })),
      },
      {
        key: 'projectRisk',
        label: 'PJ全体リスク',
        type: 'multiselect',
        options: [
          { value: 'true', label: '該当あり' },
          { value: 'false', label: '該当なし' },
        ],
      },
      { key: 'date_from', label: '期限From', type: 'date' },
      { key: 'date_to', label: '期限To', type: 'date' },
      {
        key: 'keyword',
        label: 'キーワード',
        type: 'text',
        className: 'pm-filter-item--keyword',
        placeholder: 'タイトル / 小項目 / 対応方針詳細',
      },
    ],
    [
      actorOptions,
      causeOptions,
      deadlineCheckOptions,
      employeeOptions,
      impactOptions,
      largeCategoryOptions,
      middleCategoryOptionsByLarge,
      oemRegionOptions,
      policyOptions,
      probabilityOptions,
      riskLevelOptions,
      selectOptions.middleCategory,
      statusOptions,
    ],
  );

  const handleExportConfirm = async (scope) => {
    const data = scope === 'filtered' ? filteredRows : rows;
    setExporting(true);
    try {
      await exportToExcel(data, BASE_COLUMNS, 'リスク管理');
    } finally {
      setExporting(false);
      setExportPopupOpen(false);
    }
  };

  const buildRiskRefs = () => ({
    statusByName,
    actorByName,
    causeByName,
    impactByName,
    probabilityByName,
    policyByName,
    largeCategoryByName,
    middleCategoryIdLookup,
    smallCategoryLookup,
    employeeByName,
    oemRegionLookup,
  });

  const handlePopupDelete = async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row.id) throw new Error('新規レコードは削除できません');
    await deleteRisk(row.id, row.updated_at_iso ?? null);
    const data = await getRisks();
    setRows(data);
  };

  const handlePopupSave = async (row) => {
    const payload = toPayload(row, buildRiskRefs(), defaultCreateValues);
    if (!payload) throw new Error('必須項目が未入力です');
    let savedId;
    try {
      if (row.id) {
        await updateRisk(row.id, payload);
        savedId = row.id;
      } else {
        const created = await createRisk(payload);
        savedId = created?.id;
      }
    } catch (e) {
      if (e.status === 409) {
        throw new Error('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      }
      throw e;
    }
    const data = await getRisks();
    setRows(data);
    if (savedId != null) {
      setHighlightedRowId(savedId);
      setTimeout(() => setHighlightedRowId(null), 5000);
    }
  };


  return (
    <>
    <DashboardInputLayout
      inputTabIndex={2}
      toolbar={
        !loading && !error ? (
          <DashboardInputToolbar
            countText={`${filteredRows.length} / ${rows.length} 件を表示`}
            exporting={exporting}
            onAdd={() => setAddSignal((prev) => prev + 1)}
            onExport={() => setExportPopupOpen(true)}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            showAdd
            showExport
            showFilters={showFilters}
            showFilterToggle
          />
        ) : null
      }
      filters={
        !loading && !error && showFilters ? (
          <AdvancedFilterPanel
            fields={riskFilterFields}
            filterMode={filterMode}
            onClear={clearFilters}
            onFieldChange={handleRiskFilterChange}
            onFilterModeChange={setFilterMode}
            values={riskFilterValues}
          />
        ) : null
      }
    >
      {loading && <div>読み込み中...</div>}
      {!loading && error && <div>{error}</div>}
      {!loading && !error && (
      <EditableRecordTable
        addButtonLabel="+ レコード追加"
        columns={columns}
        createEmptyRow={() => createEmptyRow(defaultCreateValues)}
        enableRowPopup
        externalAddSignal={addSignal}
        highlightedRowId={highlightedRowId}
        initialRows={filteredRows}
        onPopupDelete={handlePopupDelete}
        onPopupSave={handlePopupSave}
        onRowsChange={setEditingRows}
        readOnly={!canEdit('ダッシュボード')}
        rowPopupTitle="リスク管理レコード詳細"
        showAddButton={false}
      />
      )}
    </DashboardInputLayout>
    <ExportConfirmPopup
      open={exportPopupOpen}
      onClose={() => setExportPopupOpen(false)}
      onExport={handleExportConfirm}
      filteredCount={filteredRows.length}
      totalCount={rows.length}
      loading={exporting}
      sheetName="リスク管理"
      filterFields={riskFilterFields}
      filterValues={riskFilterValues}
    />
    </>
  );
}
